import { isString } from 'class-validator';

import { Maybe } from './globals';

/**
 * `X-UI-*` request headers our UIs attach so backend telemetry can be sliced by what the user was
 * actually looking at. `X-UI-AppName` and `X-UI-Version` are axios defaults set once per app; the
 * two headers below describe a single screen's current view, so they are set per request.
 */

/** Layout that issued the request, e.g. the Helios appointment grid vs. the flat list. */
export const UI_VIEW_MODE_HEADER = 'X-UI-ViewMode';

/** Amount of time the view covers, e.g. a single day vs. a whole month. */
export const UI_VIEW_SPAN_HEADER = 'X-UI-ViewSpan';

export enum UiViewMode {
  GRID = 'grid',
  LIST = 'list'
}

export enum UiViewSpan {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month'
}

/** Built once: the calendar reads `GET /appointment` often, and each read normalizes 2 headers. */
const UI_VIEW_MODE_LOOKUP = new Map<string, UiViewMode>(
  Object.values(UiViewMode).map(value => [value, value] as const)
);

const UI_VIEW_SPAN_LOOKUP = new Map<string, UiViewSpan>(
  Object.values(UiViewSpan).map(value => [value, value] as const)
);

/**
 * These values reach Datadog as tag values, so an unrecognized one is dropped instead of forwarded:
 * a tag whose value set is not bounded by this enum is both unusable as a facet and expensive.
 */
function normalizeHeaderValue<AllowedValue extends string>(
  value: unknown,
  allowedValues: ReadonlyMap<string, AllowedValue>
): Maybe<AllowedValue> {
  if (!isString(value)) {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();

  return allowedValues.get(normalizedValue) ?? null;
}

export function normalizeUiViewMode(value: unknown): Maybe<UiViewMode> {
  return normalizeHeaderValue(value, UI_VIEW_MODE_LOOKUP);
}

export function normalizeUiViewSpan(value: unknown): Maybe<UiViewSpan> {
  return normalizeHeaderValue(value, UI_VIEW_SPAN_LOOKUP);
}
